import React from 'react';
import { Button } from '../components/ui/Button';
import { Footer } from '../components/Footer';
import { Clock, HeartCrack, XCircle, CheckCircle2, Utensils, ShoppingCart, ListChecks, Map, BookOpen, Star, ChevronDown } from 'lucide-react';
export function LandingPage() {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <main className="min-h-screen w-full overflow-x-hidden" data-id="element-57">
      {/* 1. HERO SECTION */}
      <section className="relative bg-brand-creamLight pt-20 pb-24 lg:pt-32 lg:pb-32 px-6" data-id="element-58">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center" data-id="element-59">
          <div className="space-y-8 z-10" data-id="element-60">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-blueLight/30 text-sky-800 font-semibold text-sm mb-4" data-id="element-61">
              <Star className="w-4 h-4 fill-current" data-id="element-62" />
              Para mamás latinas en USA
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-brand-gray" data-id="element-63">
              Adiós a la Culpa del Domingo: El Sistema de{' '}
              <span className="text-brand-amber" data-id="element-64">"Ensamble"</span> para
              Loncheras en 5 Minutos
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed font-medium" data-id="element-65">
              Prepara almuerzos escolares nutritivos usando solo productos de
              Walmart, Target y Trader Joe's, sin encender la estufa y sin
              perder tu descanso.
            </p>
            <div className="pt-4" data-id="element-66">
              <Button onClick={scrollToPricing} className="w-full md:w-auto text-xl py-5 px-10" data-id="element-67">
                ¡QUIERO MI COPIA AHORA!
              </Button>
              <p className="text-sm text-gray-500 mt-4 flex items-center gap-2 justify-center md:justify-start" data-id="element-68">
                <CheckCircle2 className="w-4 h-4 text-brand-green" data-id="element-69" />
                Acceso inmediato. Pago seguro.
              </p>
            </div>
          </div>
          <div className="relative lg:ml-10 z-10" data-id="element-70">
            {/* Decorative background blob */}
            <div className="absolute inset-0 bg-brand-blueLight rounded-full blur-3xl opacity-50 transform scale-110 -z-10" data-id="element-71"></div>
            <img src="https://cdn.magicpatterns.com/uploads/1TW5aJgAHoohr7BRjGT9p2/private_openrouter-generated-image-1773592822664.png" alt="Lonchera Master USA Digital Guide on iPad" className="w-full max-w-md mx-auto rounded-2xl shadow-soft transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-72" />
          </div>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="bg-white py-20 px-6" data-id="element-73">
        <div className="max-w-3xl mx-auto text-center space-y-8" data-id="element-74">
          <Clock className="w-16 h-16 text-brand-amber mx-auto opacity-80" data-id="element-75" />
          <p className="text-2xl md:text-3xl font-heading font-semibold leading-relaxed text-brand-gray" data-id="element-76">
            Son las 8:30 PM de un domingo. Estás agotada tras una semana larga,
            pero esa pregunta martilla tu cabeza:{' '}
            <span className="text-brand-amber italic" data-id="element-77">
              "¿Qué les voy a poner de lunch mañana?"
            </span>
          </p>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed" data-id="element-78">
            Miras la nevera, ves ingredientes sueltos, y terminas resignada
            armando el mismo sándwich aburrido de siempre o enviándolos con
            dinero para pizza procesada.
          </p>
          <div className="p-6 bg-brand-cream rounded-2xl inline-block mt-8 shadow-card" data-id="element-79">
            <p className="text-lg font-bold text-brand-gray flex items-center gap-3" data-id="element-80">
              <HeartCrack className="w-6 h-6 text-red-400" data-id="element-81" />
              No es falta de amor, es cansancio extremo.
            </p>
          </div>
          <p className="text-lg text-gray-600" data-id="element-82">
            El ritmo de vida en Estados Unidos no perdona, y las guías de
            nutrición tradicionales parecen estar escritas para personas que no
            trabajan.
          </p>
        </div>
      </section>

      {/* 3. THE PROBLEM & WHY IT DIDN'T WORK */}
      <section className="bg-brand-cream py-24 px-6" data-id="element-83">
        <div className="max-w-5xl mx-auto" data-id="element-84">
          <div className="text-center mb-16" data-id="element-85">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-86">
              El dilema diario de la mamá latina
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-87">
              Quieres que tus hijos coman saludable para que rindan en la
              escuela, pero las recetas de "mamá perfecta" en redes sociales
              requieren horas de picar vegetales, cocinar proteínas y una
              limpieza eterna de la cocina.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start" data-id="element-88">
            <div className="bg-white p-8 rounded-3xl shadow-card" data-id="element-89">
              <h3 className="text-2xl font-bold mb-6 text-brand-gray border-b pb-4" data-id="element-90">
                El resultado de siempre:
              </h3>
              <ul className="space-y-4" data-id="element-91">
                <li className="flex gap-4 items-start" data-id="element-92">
                  <XCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" data-id="element-93" />
                  <span className="text-lg text-gray-700" data-id="element-94">
                    Fatiga mental al final del día.
                  </span>
                </li>
                <li className="flex gap-4 items-start" data-id="element-95">
                  <XCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" data-id="element-96" />
                  <span className="text-lg text-gray-700" data-id="element-97">
                    Desperdicio de comida que se pudre en el cajón de la nevera.
                  </span>
                </li>
                <li className="flex gap-4 items-start" data-id="element-98">
                  <XCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" data-id="element-99" />
                  <span className="text-lg text-gray-700" data-id="element-100">
                    Esa punzada de culpa al cerrar una lonchera que sabes que no
                    es la mejor, pero es "lo que hubo".
                  </span>
                </li>
              </ul>
            </div>

            <div className="space-y-6" data-id="element-101">
              <h3 className="text-2xl font-bold mb-6 text-brand-gray" data-id="element-102">
                ¿Por qué lo que has intentado no funciona?
              </h3>
              {['Las recetas de Pinterest usan ingredientes que no encuentras en tu Walmart local o son demasiado caros.', "Intentar cocinar 'desde cero' todas las noches te roba el poco tiempo que tienes para descansar o estar con tu familia.", 'Los almuerzos comprados en la cafetería escolar están cargados de sodio y carbohidratos simples que causan sueño a mitad del día.', 'Comprar cajas de Lunchables es rápido, pero te hace sentir que estás fallando en la nutrición de tus hijos.'].map((item, i) => <div key={i} className="flex gap-4 items-start bg-white/50 p-4 rounded-xl" data-id="element-103">
                  <div className="bg-red-100 p-2 rounded-full shrink-0" data-id="element-104">
                    <XCircle className="w-5 h-5 text-red-500" data-id="element-105" />
                  </div>
                  <p className="text-gray-700 font-medium" data-id="element-106">{item}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 4. THE UNIQUE MECHANISM */}
      <section className="bg-brand-green text-white py-24 px-6 relative overflow-hidden" data-id="element-107">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-108">
          <Utensils className="absolute top-10 left-10 w-32 h-32 transform -rotate-12" data-id="element-109" />
          <ShoppingCart className="absolute bottom-10 right-10 w-40 h-40 transform rotate-12" data-id="element-110" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-111">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white" data-id="element-112">
            Presentamos el Sistema de Ensamble 515
          </h2>
          <p className="text-xl md:text-2xl font-medium leading-relaxed mb-8 text-green-50" data-id="element-113">
            Este no es un libro de cocina. Es una guía de logística inteligente.
            El secreto no está en cocinar, sino en saber qué comprar.
          </p>
          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20" data-id="element-114">
            <p className="text-lg md:text-xl leading-relaxed" data-id="element-115">
              Aprovechamos los productos precortados, lavados y listos para
              servir de las tiendas que ya visitas (Walmart, Target, Trader
              Joe's) para que solo tengas que unir piezas como si fuera un
              rompecabezas nutricional.
            </p>
          </div>
        </div>
      </section>

      {/* 5. WHAT YOU GET */}
      <section className="bg-white py-24 px-6" data-id="element-116">
        <div className="max-w-6xl mx-auto" data-id="element-117">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16" data-id="element-118">
            ¿Qué incluye Lonchera Master USA?
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-119">
            {[{
            icon: BookOpen,
            title: 'La Guía Digital',
            desc: 'El manual paso a paso para dominar el supermercado americano.'
          }, {
            icon: Map,
            title: 'El Mapa de Pasillos',
            desc: 'Selección exacta de marcas (Good & Gather, Great Value, etc.) que son saludables y económicas.'
          }, {
            icon: Utensils,
            title: '20 Menús de Ensamble',
            desc: 'Combinaciones listas para un mes completo de clases sin repetir.'
          }, {
            icon: ShoppingCart,
            title: 'Lista de Compras Maestra',
            desc: 'Dividida por tienda para que tu visita al súper dure la mitad del tiempo.'
          }, {
            icon: ListChecks,
            title: 'Checklist de Empaque',
            desc: 'Cómo organizar tu cocina para que el lunch se arme casi solo.'
          }].map((item, i) => <div key={i} className="bg-brand-creamLight p-8 rounded-3xl shadow-card hover:shadow-soft transition-shadow border border-amber-100" data-id="element-120">
                <div className="bg-brand-amber/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-6" data-id="element-121">
                  <item.icon className="w-7 h-7 text-brand-amber" data-id="element-122" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-brand-gray" data-id="element-123">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-124">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. WHO THIS IS FOR / NOT FOR */}
      <section className="bg-brand-cream py-24 px-6" data-id="element-125">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-126">
          {/* For */}
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-card border-t-8 border-brand-green" data-id="element-127">
            <h3 className="text-2xl font-bold mb-8 flex items-center gap-3" data-id="element-128">
              <CheckCircle2 className="w-8 h-8 text-brand-green" data-id="element-129" />
              Esto ES para ti si:
            </h3>
            <ul className="space-y-5" data-id="element-130">
              {['Mamás latinas en USA que trabajan y tienen poco tiempo.', 'Padres que buscan opciones saludables pero no saben qué comprar en el supermercado.', 'Familias que quieren reducir el gasto en comida rápida y el desperdicio en casa.', "Personas que se sienten abrumadas por la 'parálisis de decisión' cada noche."].map((item, i) => <li key={i} className="flex gap-4 items-start" data-id="element-131">
                  <CheckCircle2 className="w-6 h-6 text-brand-green shrink-0 mt-0.5" data-id="element-132" />
                  <span className="text-gray-700 font-medium" data-id="element-133">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* Not For */}
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-card border-t-8 border-gray-300" data-id="element-134">
            <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-gray-500" data-id="element-135">
              <XCircle className="w-8 h-8 text-gray-400" data-id="element-136" />
              Esto NO es para ti si:
            </h3>
            <ul className="space-y-5 opacity-80" data-id="element-137">
              {['Personas que disfrutan pasar 2 horas diarias cocinando desde cero.', 'Familias que no tienen acceso a supermercados principales en USA.', 'Quienes buscan recetas gourmet de alta complejidad.'].map((item, i) => <li key={i} className="flex gap-4 items-start" data-id="element-138">
                  <XCircle className="w-6 h-6 text-gray-400 shrink-0 mt-0.5" data-id="element-139" />
                  <span className="text-gray-600" data-id="element-140">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* 7. HOW IT WORKS */}
      <section className="bg-white py-24 px-6" data-id="element-141">
        <div className="max-w-4xl mx-auto" data-id="element-142">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16" data-id="element-143">
            ¿Cómo funciona?
          </h2>
          <div className="space-y-12 relative before:absolute before:inset-0 before:ml-8 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-brand-amber before:via-brand-amber before:to-transparent" data-id="element-144">
            {[{
            num: '1',
            text: 'Descargas la guía y revisas la lista de compras según tu tienda favorita.'
          }, {
            num: '2',
            text: "Compras los 'Héroes de Ensamble' que ya vienen listos para usar."
          }, {
            num: '3',
            text: 'El domingo (o cada noche) sigues nuestra fórmula de 5 minutos para armar la lonchera sin ensuciar ni una sartén.'
          }].map((step, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-145">
                <div className="flex items-center justify-center w-16 h-16 rounded-full border-4 border-white bg-brand-amber text-white font-bold text-2xl shadow-soft shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-146">
                  {step.num}
                </div>
                <div className="w-[calc(100%-5rem)] md:w-[calc(50%-3rem)] bg-brand-creamLight p-6 rounded-2xl shadow-card border border-amber-50" data-id="element-147">
                  <p className="text-lg font-medium text-gray-700" data-id="element-148">
                    {step.text}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. PROOF / BELIEVABILITY */}
      <section className="bg-brand-blueLight/30 py-24 px-6" data-id="element-149">
        <div className="max-w-4xl mx-auto text-center space-y-8" data-id="element-150">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-gray" data-id="element-151">
            ¿Qué cambia en 7 días?
          </h2>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-medium" data-id="element-152">
            El próximo domingo a las 8:10 PM ya habrás terminado las loncheras
            de toda la semana.
          </p>
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-soft mt-8 text-left md:text-center" data-id="element-153">
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-154">
              Tu cocina estará limpia, tus hijos tendrán comida real en sus
              maletas y tú estarás sentada en el sofá disfrutando de tu programa
              favorito o simplemente descansando.
            </p>
            <p className="text-2xl font-bold text-brand-amber" data-id="element-155">
              Pasarás de la "decisión agónica" a la "ejecución automática".
            </p>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-white py-24 px-6" data-id="element-156">
        <div className="max-w-3xl mx-auto" data-id="element-157">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16" data-id="element-158">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-6" data-id="element-159">
            {[{
            q: '¿Necesito saber cocinar?',
            a: 'No. Si sabes abrir un paquete y poner comida en un recipiente, puedes usar este sistema.'
          }, {
            q: '¿Es caro comprar comida ya preparada?',
            a: 'Es más barato que comprar comida rápida y mucho más económico que dejar que la comida cruda se dañe en tu nevera por falta de tiempo para cocinarla.'
          }, {
            q: '¿Los menús son aptos para niños con alergias?',
            a: 'Damos opciones variadas para que puedas sustituir cualquier ingrediente según las necesidades de tu hijo.'
          }, {
            q: '¿Recibiré un libro físico?',
            a: 'Es un producto digital de acceso inmediato. Puedes leerlo en tu celular mientras haces las compras.'
          }].map((faq, i) => <div key={i} className="bg-brand-creamLight rounded-2xl p-6 border border-amber-100" data-id="element-160">
                <h3 className="text-xl font-bold text-brand-gray mb-3 flex items-start gap-3" data-id="element-161">
                  <span className="text-brand-amber mt-1" data-id="element-162">Q.</span>
                  {faq.q}
                </h3>
                <p className="text-gray-600 pl-8 leading-relaxed" data-id="element-163">{faq.a}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 10. OFFER + CTA */}
      <section id="pricing" className="bg-brand-cream py-24 px-6" data-id="element-164">
        <div className="max-w-3xl mx-auto text-center" data-id="element-165">
          <div className="bg-white p-8 md:p-16 rounded-[3rem] shadow-soft border-4 border-brand-amber/20 relative" data-id="element-166">
            {/* Decorative badge */}
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-brand-green text-white px-6 py-2 rounded-full font-bold text-sm tracking-wide shadow-md" data-id="element-167">
              OFERTA ESPECIAL
            </div>

            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-brand-gray" data-id="element-168">
              Recupera tu paz mental y asegura la salud de tus hijos hoy mismo.
            </h2>
            <p className="text-xl text-gray-600 mb-10" data-id="element-169">
              No permitas que una lonchera sea la causa de tu estrés diario.
            </p>

            <div className="mb-10" data-id="element-170">
              <p className="text-gray-500 font-medium uppercase tracking-wider mb-2" data-id="element-171">
                Inversión única
              </p>
              <div className="text-6xl md:text-7xl font-extrabold text-brand-amber font-heading" data-id="element-172">
                $6<span className="text-3xl text-gray-400" data-id="element-173">.99</span>{' '}
                <span className="text-2xl text-gray-400 font-medium" data-id="element-174">USD</span>
              </div>
              <p className="text-sm text-gray-500 mt-4 italic" data-id="element-175">
                (Menos de lo que cuesta un café con leche).
              </p>
            </div>

            <Button href="https://pay.hotmart.com/E104952880S?off=zp804515&bid=1773787463220" className="w-full md:w-auto text-xl py-6 px-12 animate-bounce-subtle" data-id="element-176">
              ¡QUIERO MI COPIA DE LONCHERA MASTER USA!
            </Button>

            <div className="mt-8 flex flex-col items-center gap-2 text-sm text-gray-500" data-id="element-177">
              <div className="flex items-center gap-2" data-id="element-178">
                <CheckCircle2 className="w-5 h-5 text-brand-green" data-id="element-179" />
                Acceso instantáneo tras la compra.
              </div>
              <div className="flex items-center gap-2" data-id="element-180">
                <CheckCircle2 className="w-5 h-5 text-brand-green" data-id="element-181" />
                Pago seguro y garantizado.
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer data-id="element-182" />
    </main>;
}